import { useState } from 'react';
import { D, formatMoney } from '@makila/fiscal';
import type { CreditAccount } from '@makila/pos-core';
import { X } from 'lucide-react';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { Operator } from '../screens/Lock';
import { ManagerApproval } from './ManagerApproval';
import { useEffect, useRef } from 'react';

function Shell(p: { title: string; onClose: () => void; children: React.ReactNode }) {
  const dlg = useRef<HTMLDialogElement>(null);
  useEffect(() => { dlg.current?.showModal(); }, []);
  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); p.onClose(); }} className="m-auto w-[min(560px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">{p.title}</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="p-5">{p.children}</div>
    </dialog>
  );
}

const dmy = (d: Date) => d.toISOString().slice(0, 10).split('-').reverse().join('/');

/**
 * Venda a crédito (FT): resumo da conta do cliente; acima do limite, um
 * gerente autoriza (decisão do utilizador, 30/10/2026).
 */
export function CreditSaleDialog(p: {
  customerName: string; total: string; account: CreditAccount; store: IdbTerminalStore; operator: Operator;
  onClose: () => void; onConfirm: (approvedBy: string | null) => Promise<void>;
}) {
  const after = new D(p.account.balance).plus(p.total);
  const over = after.greaterThan(p.account.limit ?? 0);
  const [approver, setApprover] = useState<Operator | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const due = new Date(Date.now() + p.account.termsDays * 86_400_000);
  return (
    <Shell title="Venda a crédito" onClose={p.onClose}>
      <p className="text-lg"><b>{p.customerName}</b> paga depois, com recibo. Vence a <b>{dmy(due)}</b> ({p.account.termsDays} dias).</p>
      <dl className="num mt-4 space-y-1 rounded-xl bg-surface-2 p-4">
        <div className="flex justify-between"><dt>Em dívida</dt><dd>{formatMoney(p.account.balance)} MT</dd></div>
        <div className="flex justify-between"><dt>Esta factura</dt><dd>{formatMoney(p.total)} MT</dd></div>
        <div className="flex justify-between border-t border-base pt-1 font-bold"><dt>Fica em dívida</dt><dd>{formatMoney(after)} MT</dd></div>
        <div className="flex justify-between text-sm text-muted"><dt>Limite</dt><dd>{formatMoney(p.account.limit ?? 0)} MT</dd></div>
      </dl>
      {over && !approver && (
        <div className="mt-4">
          <p role="alert" className="mb-3 rounded-xl bg-amber-100 p-3 text-sm text-amber-900">Passa o limite de crédito em {formatMoney(after.minus(p.account.limit ?? 0))} MT: um gerente tem de autorizar.</p>
          <ManagerApproval store={p.store} operator={p.operator} why="Venda acima do limite de crédito" onApproved={setApprover} />
        </div>
      )}
      {approver && over && <p className="mt-3 text-sm text-muted">Autorizado por {approver.name}</p>}
      {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
      <button disabled={busy || (over && !approver)} onClick={async () => {
        setBusy(true); setError(null);
        try { await p.onConfirm(over ? approver!.id : null); } catch (e) { setError((e as Error).message); setBusy(false); }
      }} className="mt-5 h-16 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Emitir factura a crédito</button>
    </Shell>
  );
}

/** Recibo: quanto o cliente paga agora da conta corrente (no máximo o que deve). */
export function ReceiptAmountDialog(p: { customerName: string; balance: string; onClose: () => void; onAmount: (amount: string) => void }) {
  const [v, setV] = useState(new D(p.balance).toFixed(2).replace('.', ','));
  const n = /^\d{1,9}([.,]\d{1,2})?$/.test(v.trim()) ? new D(v.trim().replace(',', '.')) : null;
  const ok = !!n && n.greaterThan(0) && n.lessThanOrEqualTo(p.balance);
  return (
    <Shell title={`Receber de ${p.customerName}`} onClose={p.onClose}>
      <p className="text-muted">Em dívida: <b className="num text-[var(--text)]">{formatMoney(p.balance)} MT</b>. Pagam-se primeiro as facturas mais antigas.</p>
      <form onSubmit={(e) => { e.preventDefault(); if (ok) p.onAmount(n!.toFixed(2)); }}>
        <div className="mt-4 flex items-center gap-2">
          <input autoFocus inputMode="decimal" aria-label="Valor a receber" value={v} onChange={(e) => setV(e.target.value)} className="num h-16 min-w-0 flex-1 rounded-xl border border-base bg-surface-2 px-4 text-right text-3xl" />
          <span className="text-2xl font-bold">MT</span>
        </div>
        {n && n.greaterThan(p.balance) && <p role="alert" className="mt-2 text-sm text-brand-700">Não pode receber mais do que o cliente deve.</p>}
        <button disabled={!ok} className="mt-5 h-16 w-full rounded-xl bg-brand-700 text-lg font-bold text-white disabled:opacity-40">Continuar para o pagamento</button>
      </form>
    </Shell>
  );
}
